import React, { createContext, useContext, useEffect, useState } from "react";

import { CoreService } from "@/services/core/CoreService";
import { LoginCredentials, User } from "@/types/auth";
import { usePermission } from "@/utils/permissionManager";
import { UserRole } from "@/utils/roleManager";

interface IAuthContext {
  user: User | null;
  login: (credentials: LoginCredentials) => Promise<User | null>;
  loginWithSso: (state: string, code: string) => Promise<User>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
  isLoading: boolean;
  permissions: {
    isCraveUser: () => boolean;
  };
  defaultCustomerId?: number;
  allowedStores?: number[];
}

const AuthContext = createContext<IAuthContext | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [defaultCustomerId, setDefaultCustomerId] = useState<number | undefined>();
  const [allowedStores, setAllowedStores] = useState<number[] | undefined>();
  const coreService = CoreService.getInstance();
  const permissions = usePermission(user?.role || UserRole.NONE);

  // Update default customer and stores when user changes
  useEffect(() => {
    if (user && !permissions.isCraveUser()) {
      setDefaultCustomerId(user.customerId);
      setAllowedStores(user.allowedStores);
    } else {
      setDefaultCustomerId(undefined);
      setAllowedStores(undefined);
    }
  }, [user, permissions]);

  // Initialize auth state from localStorage
  useEffect(() => {
    const initializeAuth = () => {
      try {
        const storedUser = coreService.getStoredUser();
        const isAuthed = coreService.isAuthenticated();

        if (storedUser && isAuthed) {
          // Ensure the stored user data matches our User interface
          const validatedUser: User = {
            id: storedUser.id,
            email: storedUser.email,
            firstName: storedUser.firstName,
            lastName: storedUser.lastName,
            role: storedUser.role,
            customerId: storedUser.customerId,
            allowedStores: storedUser.allowedStores || [],
            token: storedUser.token || "",
          };

          setUser(validatedUser);
          setIsAuthenticated(true);
        }
      } catch {
        // Clear potentially corrupted auth state
        coreService.logout();
      } finally {
        setIsInitialized(true);
      }
    };

    initializeAuth();
  }, []);

  const login = React.useCallback(async (credentials: LoginCredentials) => {
    try {
      const response = await coreService.login(credentials);
      if (response.user) {
        const user = response.user;
        setUser(user);
        localStorage.setItem("user", JSON.stringify(user));

        setIsAuthenticated(true);
        return user;
      }
      return null;
    } catch (error) {
      throw error;
    }
  }, []);

  const logout = React.useCallback(async () => {
    try {
      coreService.logout();
      setUser(null);
      localStorage.removeItem("user");
      setIsAuthenticated(false);
      setDefaultCustomerId(undefined);
      setAllowedStores(undefined);
    } catch (error) {
      throw error;
    }
  }, []);

  const loginWithSso = React.useCallback(
    async (state: string, code: string) => {
      const response = await coreService.completeSso(state, code);
      setUser(response.user);
      localStorage.setItem("user", JSON.stringify(response.user));
      setIsAuthenticated(true);
      return response.user;
    },
    [coreService],
  );

  if (!isInitialized) {
    return null; // Or a loading spinner if you prefer
  }

  const value = {
    user,
    login,
    loginWithSso,
    logout,
    isAuthenticated,
    isLoading: !isInitialized,
    permissions,
    defaultCustomerId,
    allowedStores,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
